import { useMemo } from 'react';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { ObjectiveFilteredEmployee } from '@/types/objectives';

import { useGetUserById } from '@/api/directory';
import { useGetObjectiveFilteredEmployees } from '@/api/objectives';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';
import {
    hasSelectedFilters,
    matchesSelectedFilters,
    toAttributeIdParams,
} from '@/utils/companyAttributeFilters';

/** What the roster and the directory both say of a person the filters keep: enough to name them. */
export type FilteredEmployee = ObjectiveFilteredEmployee;

/**
 * The employees a company-attribute filter resolves to, read from the roster. Without a filter
 * nothing is requested and `filterEmployeeIds` stays undefined, which callers read as "everyone"; a
 * filter that holds no one in the reader's reach is an empty set, which keeps no one.
 *
 * The roster holds the people whose objectives the reader sees, and it answers a manager with their
 * reports, never the manager. Both are put back, every family picked combining with the others: a
 * picked manager, so their own objectives sit beside their line's, when the roster asked on every
 * other family holds them -- being picked meets the manager family, and a manager outside the
 * reader's reach is in no roster -- and the reader, as the directory matches them, when they meet
 * every family, their own pick meeting the manager's.
 */
export const useRosterFilter = (companyFilters: SelectedFilters | undefined) => {
    const isFilterActive = hasSelectedFilters(companyFilters);
    const { data: reader } = useGetUserById({ userId: 'me' });
    const pickedManagerIds = useMemo(
        () => (companyFilters?.managers ?? []).map(({ id }) => id),
        [companyFilters],
    );
    const otherFamilies = useMemo(
        () => ({ ...(companyFilters ?? defaultFiltersState), managers: [] }),
        [companyFilters],
    );
    const otherFamiliesParams = useMemo(() => toAttributeIdParams(otherFamilies), [otherFamilies]);
    const asksPickedManagers = pickedManagerIds.some((id) => id !== reader?.id);
    const {
        data: rosterUnderOtherFamilies,
        isLoading: isLoadingPickedManagers,
        isPlaceholderData: isPickedManagersPlaceholder,
        error: pickedManagersError,
    } = useGetObjectiveFilteredEmployees(otherFamiliesParams, asksPickedManagers);
    const rosterParams = useMemo(
        () => toAttributeIdParams(companyFilters ?? defaultFiltersState),
        [companyFilters],
    );
    const {
        data: filteredRoster,
        isLoading,
        isPlaceholderData,
        error,
    } = useGetObjectiveFilteredEmployees(rosterParams, isFilterActive);

    const filterEmployees = useMemo<FilteredEmployee[] | undefined>(() => {
        if (!isFilterActive || !companyFilters) {
            return undefined;
        }

        const keptManagers = asksPickedManagers
            ? (rosterUnderOtherFamilies ?? []).filter(({ id }) => pickedManagerIds.includes(id))
            : [];
        const isReaderMatched =
            !!reader &&
            matchesSelectedFilters(
                reader,
                pickedManagerIds.includes(reader.id) ? otherFamilies : companyFilters,
            );
        const kept = [
            ...(filteredRoster ?? []),
            ...keptManagers,
            ...(isReaderMatched ? [reader] : []),
        ];

        return [...new Map(kept.map((employee) => [employee.id, employee])).values()];
    }, [
        isFilterActive,
        companyFilters,
        asksPickedManagers,
        rosterUnderOtherFamilies,
        pickedManagerIds,
        reader,
        otherFamilies,
        filteredRoster,
    ]);
    const filterEmployeeIds = useMemo(
        () => filterEmployees && new Set(filterEmployees.map(({ id }) => id)),
        [filterEmployees],
    );

    return {
        isFilterActive,
        filterEmployees,
        filterEmployeeIds,
        // The roster keeps the previous filter's people while the new one loads: those are not an
        // answer for the filter now selected.
        isResolvingFilter:
            isFilterActive &&
            (isLoading ||
                isPlaceholderData ||
                (asksPickedManagers && (isLoadingPickedManagers || isPickedManagersPlaceholder))),
        hasFilterError:
            isFilterActive && (!!error || (asksPickedManagers && !!pickedManagersError)),
    };
};
